import { z } from 'zod';
import { assetKindSchema } from './enums.js';
/**
 * Contrats du DÉDOUBLONNAGE EXACT (copies pixel-identiques). Source unique front + back.
 *
 * Distinct du dédoublonnage QUASI (phash, cf. `dedup.ts`) : ici on cible les copies
 * EXACTES (même `identity_hash` = mêmes pixels décodés — copie octet-à-octet OU
 * ré-encodage pixel-identique). Au scan, ces copies sont COLLAPSÉES dans `assets`
 * (unicité `unique(library_id, identity_hash)`) mais leurs chemins physiques sont
 * désormais inventoriés dans `asset_file_paths` → on peut les présenter et supprimer.
 *
 * - `exactDupPathSchema`            : un chemin physique présent d'un groupe.
 * - `exactDupGroupSchema`           : une image et ses ≥2 chemins présents.
 * - `exactDupGroupsResponseSchema`  : page de groupes + compteurs.
 * - `exactDupSummarySchema`         : résumé (badge/tête d'écran).
 * - `exactDupApplyRequestSchema`    : corps de POST …/dedup/exact/apply (décisions).
 * - `exactDupApplyResultSchema`     : ventilation d'un job `dedup_exact_apply`.
 *
 * SÉCURITÉ : ces schémas ne font QUE valider les entrées. La garde anti-traversée
 * DÉFINITIVE lors de la suppression reste le `safeJoin(root, rel)` côté job.
 */
// ---------------------------------------------------------------------------
// Lecture des groupes (GET /api/libraries/:id/dedup/exact)
// ---------------------------------------------------------------------------
/**
 * Un chemin physique PRÉSENT d'un groupe de copies exactes. `isCanonical` = ce chemin
 * est celui porté par l'asset (`assets.rel_path`) de même identité (le représentant
 * indexé). `fileHash` distingue une copie octet-à-octet d'un ré-encodage.
 */
export const exactDupPathSchema = z.object({
    relPath: z.string(),
    fileHash: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    isCanonical: z.boolean(),
});
/**
 * Un groupe de copies exactes : l'image (via son asset canonique, pour la vignette
 * `GET /api/assets/:id/thumbnail?size=256` adressée par `identityHash`) et ses ≥2
 * chemins physiques présents sur le disque.
 */
export const exactDupGroupSchema = z.object({
    identityHash: z.string(),
    /**
     * Id de l'asset CANONIQUE de cette identité — pour adresser la vignette
     * (`GET /api/assets/:id/thumbnail?size=256`). `null` si l'asset a été désindexé
     * (les chemins physiques survivent à la désindexation → le groupe reste, mais sans
     * vignette : l'UI affiche alors un repère générique).
     */
    assetId: z.uuid().nullable(),
    kind: assetKindSchema,
    width: z.number().int().nonnegative().nullable(),
    height: z.number().int().nonnegative().nullable(),
    filename: z.string(),
    paths: z.array(exactDupPathSchema).min(2),
});
/**
 * Page de groupes de copies exactes. `total` = nombre total de groupes (pagination) ;
 * `redundantCount` = Σ (nb de chemins − 1) sur TOUS les groupes (fichiers supprimables
 * en gardant 1 exemplaire par image).
 */
export const exactDupGroupsResponseSchema = z.object({
    groups: z.array(exactDupGroupSchema),
    total: z.number().int().nonnegative(),
    redundantCount: z.number().int().nonnegative(),
});
/** Bornes de pagination des groupes (query `limit`/`offset`). */
export const EXACT_DUP_LIMIT_MAX = 50;
export const EXACT_DUP_LIMIT_DEFAULT = 50;
/** Query de `GET /api/libraries/:id/dedup/exact` : pagination bornée. */
export const exactDupGroupsQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(EXACT_DUP_LIMIT_MAX)
        .optional()
        .default(EXACT_DUP_LIMIT_DEFAULT),
    offset: z.coerce.number().int().nonnegative().optional().default(0),
});
// ---------------------------------------------------------------------------
// Résumé (GET /api/libraries/:id/dedup/exact/summary)
// ---------------------------------------------------------------------------
/**
 * Résumé des copies exactes de la bibliothèque. `groupCount` = nb d'images à ≥2
 * chemins présents ; `redundantCount` = Σ (chemins − 1) ; `lastScanAt` = date du
 * dernier scan TERMINÉ (`max(finished_at)` du dernier job `scan` `done`), ou `null`
 * si jamais scanné (« inventaire à jour au … » vs « jamais scanné »).
 */
export const exactDupSummarySchema = z.object({
    groupCount: z.number().int().nonnegative(),
    redundantCount: z.number().int().nonnegative(),
    lastScanAt: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Application des décisions (POST /api/libraries/:id/dedup/exact/apply)
// ---------------------------------------------------------------------------
/**
 * Une décision par image : conserver le chemin `keepRelPath` et supprimer TOUS les
 * autres chemins présents de cette identité. `keepRelPath` DOIT être l'un des chemins
 * présents (validé côté job — sinon skip, aucune suppression).
 */
export const exactDupDecisionSchema = z.object({
    identityHash: z.string().min(1),
    keepRelPath: z.string().min(1),
});
/** Nombre max de décisions par requête (garde-fou anti-abus). */
export const EXACT_DUP_DECISIONS_MAX = 5000;
/**
 * Corps de `POST /api/libraries/:id/dedup/exact/apply`. `decisions` = liste NON VIDE
 * (1..N, bornée) de décisions par image. La SUPPRESSION est une ÉCRITURE : la route
 * exige l'accès écriture ET `LIBRARY_WRITABLE` (409 sinon). `dryRun` : aperçu, ne
 * supprime rien.
 */
export const exactDupApplyRequestSchema = z.object({
    decisions: z.array(exactDupDecisionSchema).min(1).max(EXACT_DUP_DECISIONS_MAX),
    dryRun: z.boolean().optional().default(false),
});
/**
 * Ventilation d'un job `dedup_exact_apply`. Chaque CHEMIN non-keeper traité tombe
 * dans une catégorie :
 *  - `deleted` : chemin supprimé du disque (ou, en dry-run, qui SERAIT supprimé).
 *  - `skipped` : fichier déjà absent du disque (ENOENT) — inventaire marqué quand même.
 *  - `failed`  : chemin refusé (traversée), décision invalide (keepRelPath absent /
 *    groupe < 2 chemins), ou échec de suppression.
 */
export const exactDupApplyResultSchema = z.object({
    deleted: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
//# sourceMappingURL=exact-dedup.js.map